import { getAcetate, getPropionate } from "../helpers/gasAnalysisReport";
import { ISampleRecordPopulated } from "./sample";

export enum CropTypes {
  TMR = "TMR",
  TMR_F = "TMR-F",
  CS = "CS",
  NAHAY = "NAHAY",
  manure = "manure",
  Hay = "Hay",
  Haylage = "Haylage",
  NASILAGE = "Nasilage",
  FreshHayGrass = "Fresh Hay/Grass",
  CornGrain = "Corn Grain",
  Straw = "Straw",
  SmallGrainSilage = "Small Grain Silage",
  SoyCommodities = "Soy Commodities",
  AnimalProteinCommodities = "Animal Protein Commodities",
  GeneralCommodities = "General Commodities",
}

export const CropTypeCodeToName = {
  1: CropTypes.Hay,
  2: CropTypes.Haylage,
  3: CropTypes.CS,
  4: CropTypes.FreshHayGrass,
  6: CropTypes.TMR,
  7: CropTypes.CornGrain,
  9: CropTypes.Straw,
  11: CropTypes.NAHAY,
  12: CropTypes.NASILAGE,
  13: CropTypes.TMR_F,
  22: CropTypes.SmallGrainSilage,
  50: CropTypes.manure,
  81: CropTypes.SoyCommodities,
  86: CropTypes.AnimalProteinCommodities,
  88: CropTypes.GeneralCommodities,
};

export const enabledCropTypes = [CropTypes.TMR, CropTypes.CS, CropTypes.NAHAY];
interface IDataField {
  fieldName: string;
  aliases?: string[];
}
export interface ChemicalField extends IDataField {
  types?: CropTypes[];
  /** NASEM feed-library column this value is written to. */
  formattedName?: string;
  requiredForGHG?: boolean;
  requiredForChemAnalysis?: boolean;
  requiredForGasReport?: boolean;
  numberOfDecimals?: number;
  unit?: string;
  rationIngredientDefault?: boolean;
  group?: ChemicalFieldGroup;
  shownInGasReportTable?: boolean;
  optionalInGasReportTable?: boolean;
}

export enum ChemicalFieldGroup {
  NAG = "NAG",
  FTI = "FTI",  
  OTHER = "Other",
}

export const ChemicalFields: ChemicalField[] = [
  {
    types: [CropTypes.TMR],
    fieldName: "F MBP/mg/g",
    aliases: ["MBP"],
    requiredForGHG: true,
    unit: "mg/g",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F OMD%DM",
    aliases: ["OMD"],
    requiredForGHG: true,
    unit: "%",
    group: ChemicalFieldGroup.FTI,
    shownInGasReportTable: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F APF",
    aliases: ["APF"],
    requiredForGHG: true,
    unit: "%",
    group: ChemicalFieldGroup.FTI,
    shownInGasReportTable: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F NA",
    aliases: ["NA"],
    requiredForGHG: true,
    group: ChemicalFieldGroup.FTI,
    shownInGasReportTable: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F C:B1/Kd/hr",
    aliases: ["C:B1", "CB1", "c-b1"],
    requiredForGHG: true,
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F C:B3/Kd/hr",
    aliases: ["C:B3", "CB3", "c-b3"],
    requiredForGHG: true,
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F uNDF48%NDF",
    aliases: ["UNDF", "undf48"],
    requiredForGHG: true,
    unit: "%",
    group: ChemicalFieldGroup.FTI,
    shownInGasReportTable: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F FRate/Kd/hr",
    requiredForGHG: true,
    aliases: ["Fast_Pool", "fast pool", "fast-pool"],
    unit: "Kd/Hr",
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F SRate/Kd/hr",
    requiredForGHG: true,
    aliases: ["Slow_Pool", "slow pool", "slow-pool", "srate"],
    unit: "Kd/Hr",
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F FPer%",
    requiredForGHG: true,
    aliases: ["Fast_Pool_%", "fast pool %", "fast-pool-%"],
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F SPer%",
    aliases: ["Slow_Pool_%", "slow pool %", "slow-pool-%", "sper"],
    requiredForGHG: true,
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F Fmax/Hr",
    aliases: ["Fast_HR", "fast hr", "fast-hr", "fmax"],
    requiredForGHG: true,
    requiredForGasReport: true,
    group: ChemicalFieldGroup.FTI,
    numberOfDecimals: 5
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F Smax/Hr",
    aliases: ["Slow_HR", "slow hr", "slow-hr", "smax"],
    requiredForGHG: true,
    requiredForGasReport: true,
    group: ChemicalFieldGroup.FTI,
    numberOfDecimals: 5
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F Asy1/Min",
    aliases: ["asym1st", "Asy1"],
    requiredForGHG: true,
    requiredForGasReport: true,
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F Asy2/Min",
    aliases: ["asym2nd", "Asy2"],
    requiredForGHG: true,
    requiredForGasReport: true,
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "F LagTime:Hr",
    aliases: ["LagTime", "lag time"],
    requiredForGHG: true,
    requiredForGasReport: true,
    group: ChemicalFieldGroup.FTI,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Hybrid",
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Ml Gas_3",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Ml Gas_6",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Ml Gas_12",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Ml Gas_24",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.manure],
    fieldName: "CH4",
  },
  {
    types: [CropTypes.TMR],
    fieldName: "CH4_3",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "CH4_6",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "CH4_12",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "CH4_24",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "C02_3",
  },
  {
    types: [CropTypes.TMR],
    fieldName: "C02_6",
  },
  {
    types: [CropTypes.TMR],
    fieldName: "C02_12",
  },
  {
    types: [CropTypes.TMR],
    fieldName: "C02_24",
  },
  {
    types: [CropTypes.TMR],
    fieldName: "H2_3",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "H2_6",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "H2_12",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "H2_24",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "ORP_3",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "ORP_6",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "ORP_12",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "ORP_24",
    requiredForGHG: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Acetate_3",
    aliases: ["Ace_3"],
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Acetate_6",
    aliases: ["Ace_6"],
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Acetate_12",
    aliases: ["Ace_12"],
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Acetate_24",
    aliases: ["Ace_24"],
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Propionate_3",
    aliases: ["Prop_3"],
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Propionate_6",
    aliases: ["Prop_6"],
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Propionate_12",
    aliases: ["Prop_12"],
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Propionate_24",
    aliases: ["Prop_24"],
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W DM",
    formattedName: "Fd_DM",
    aliases: ["DM"],
    requiredForChemAnalysis: true,
    unit: "%",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W CP%DM",
    formattedName: "Fd_CP",
    aliases: ["CP", "Protein"],
    unit: "%",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },

  // {
  //   types: [CropTypes.TMR],
  //   fieldName: "NDICP",
  //   aliases: ["NDFICP"],
  //   requiredForChemAnalysis: true,
  //   unit: "%",
  //   rationIngredientDefault: true,
  //   group: ChemicalFieldGroup.NAG,
  // },
  {
    types: [CropTypes.TMR],
    fieldName: "W ADF%DM",
    formattedName: "Fd_ADF",
    aliases: ["ADF"],
    requiredForChemAnalysis: true,
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },
  /* {
    types: [CropTypes.TMR],
    fieldName: "W ANDF",
    formattedName: "Fd_NDF",
    aliases: ["aNDF"],
    requiredForChemAnalysis: true,
    unit: "%",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    types: [CropTypes.TMR],
    fieldName: "W NDF%DM",
    formattedName: "Fd_NDF",
    aliases: ["NDF", "aNDF"],
    unit: "%",
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W LIGN%DM",
    formattedName: "Fd_Lg",
    aliases: ["Lignin", "Lign"],
    requiredForChemAnalysis: true,
    unit: "%",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },
  {
    types: [CropTypes.TMR, CropTypes.manure],
    fieldName: "W STARCH%DM",
    formattedName: "Fd_St",
    aliases: ["Starch"],
    requiredForChemAnalysis: true,
    unit: "%",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },
  /* {
    types: [CropTypes.TMR],
    fieldName: "W SUGAR-WSC",
    formattedName: "Fd_WSC",
    aliases: ["Sugar_WSC"],
    requiredForChemAnalysis: true,
    unit: "%",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W WSC%DM",
    formattedName: "Fd_WSC",
    aliases: ["WSC", "Sugar_WSC"],
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
    optionalInGasReportTable: true
  },
  {
    types: [CropTypes.TMR],
    fieldName: "Fat_EE",
    formattedName: "Fd_CFat",
    aliases: [],
    requiredForChemAnalysis: true,
    unit: "%",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W ASH%DM",
    formattedName: "Fd_Ash",
    aliases: ["Ash"],
    requiredForChemAnalysis: true,
    unit: "%",
    rationIngredientDefault: true,
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },
  /* {
    types: [CropTypes.TMR],
    fieldName: "W NDF30",
    aliases: ["NDF30", "DNDFR30T"],
    requiredForChemAnalysis: true,
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    types: [CropTypes.TMR],
    fieldName: "W NDF48",
    aliases: ["NDF48", "DNDFR48T"],
    numberOfDecimals: 3,
    // requiredForChemAnalysis: true,
  }, */

  /* {
    types: [CropTypes.TMR],
    fieldName: "W NDF120",
    aliases: ["NDF120", "DNDFR120"],
    requiredForChemAnalysis: true,
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    types: [CropTypes.TMR],
    fieldName: "NSC",
    aliases: ["NSC%DM", "W NSC%DM"],
    requiredForChemAnalysis: true,
    unit: "%",
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },
  {
    types: [CropTypes.manure],
    fieldName: "N20",
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W ALP%FA",
    aliases: ["ALP"],
    unit: "%FA",
    group: ChemicalFieldGroup.NAG,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W DEMCAL/KG",
    formattedName: "Fd_DE_Base",
    aliases: ["DEMCAL"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W LIN%FA",
    formattedName: "Fd_C182_FA",
    aliases: ["LIN"],
    unit: "%FA",
    group: ChemicalFieldGroup.NAG,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W MEMCAL/KG",
    aliases: ["MEMCAL"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W MEMJCAL/KG",
    aliases: ["MEMJCAL"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W MYR%FA",
    formattedName: "Fd_C140_FA",
    aliases: ["MYR"],
    unit: "%FA",
    group: ChemicalFieldGroup.NAG,
  },
  {
    types: [CropTypes.TMR],
    fieldName: "W OLE%FA",
    formattedName: "Fd_C181c_FA",
    aliases: ["OLE"],
    unit: "%FA",
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W NEGMCAL/KG",
    aliases: ["NEGMCAL"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W NELMCAL/KG",
    aliases: ["NELMCAL"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W NEMCAL/KG",
    aliases: ["NEMCAL"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W AA%DM",
    aliases: ["AA"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W CA%DM",
    formattedName: "Fd_Ca",
    aliases: ["CA"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W CALCIUM",
    formattedName: "Fd_Ca",
    aliases: ["CALCIUM"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W CHLORIDE",
    formattedName: "Fd_Cl",
    aliases: ["CHLORIDE", "W CL%DM"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W COPPER",
    formattedName: "Fd_Cu",
    aliases: ["COPPER"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W CP%DM",
    formattedName: "Fd_CP",
    aliases:[],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W CRUDE-PROTEIN",
    formattedName: "Fd_CP",
    aliases: ["CRUDE-PROTEIN"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W DSHD800",
    aliases: ["DSHD800"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W ESC%DM",
    aliases: ["ESC"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W FRC%DM",
    aliases: ["FRC"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W GLU%DM",
    aliases: ["GLU"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W IRON",
    formattedName: "Fd_Fe",
    aliases: ["IRON"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W K%DM",
    formattedName: "Fd_K",
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W LYSINE",
    formattedName: "Fd_Lys_CP",
    aliases: ["LYSINE"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W MAGNESIUM",
    formattedName: "Fd_Mg",
    aliases: ["MAGNESIUM"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W MANGANESE",
    formattedName: "Fd_Mn",
    aliases: ["MANGANESE"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W METHIONINE",
    formattedName: "Fd_Met_CP",
    aliases: ["METHIONINE"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W MG%DM",
    formattedName: "Fd_Mg",
    aliases: ["MG%DM"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W ML-GAS-24",
    aliases: ["ML-GAS-24"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W NDF-CP",
    formattedName: "Fd_NDFIP",
    aliases: ["NDF-CP"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W NDFD120",
    aliases: ["NDFD120"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W NDFD240",
    aliases: ["NDFD240"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W NDFD-24",
    aliases: ["NDFD-24"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W NDFD30",
    aliases: ["NDFD30"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W NDFD48",
    formattedName: "Fd_DNDF48_NDF",
    aliases: ["NDFD48"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W NDFICP%DM",
    formattedName: "Fd_NDFIP",
    aliases: ["NDFICP"],
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
    optionalInGasReportTable: true
  },
  {
    fieldName: "W NH3N%DM",
    aliases: ["NH3N"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W NOMD800",
    aliases: ["NOMD800"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W PAL%FA",
    formattedName: "Fd_C160_FA",
    aliases: ["PAL"],
    unit: "%FA",
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W PH",
    aliases: ["PH"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W PHOSPHORUS",
    formattedName: "Fd_P",
    aliases: ["PHOSPHORUS", "W P%DM"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W POTASSIUM",
    formattedName: "Fd_K",
    aliases: ["POTASSIUM"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W RFQ",
    aliases: ["RFQ"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W SD3%DM",
    aliases: ["SD3"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W SD7%DM",
    aliases: ["SD7"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W SODIUM",
    formattedName: "Fd_Na",
    aliases: ["SODIUM"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W SOL-PROTEIN",
    formattedName: "Fd_CPs_CP",
    aliases: ["SOL-PROTEIN"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W STE%FA",
    formattedName: "Fd_C180_FA",
    aliases: ["STE"],
    unit: "%FA",
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W SUCROSE",
    aliases: ["SUCROSE"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W SULPHUR",
    formattedName: "Fd_S",
    aliases: ["SULPHUR", "W S%DM"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W TFAA%DM",
    formattedName: "Fd_FA",
    aliases: ["TFAA"],
    group: ChemicalFieldGroup.NAG,
  },
  /* {
    fieldName: "W TOTAL-FATTY-ACIDS",
    formattedName: "Fd_FA",
    aliases: ["TOTAL-FATTY-ACIDS"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W UIP-(BYPASS)",
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W UNDFOM120",
    aliases: ["UNDFOM120"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W UNDFOM240",
    aliases: ["UNDFOM240"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W UNDFOM30",
    aliases: ["UNDFOM30"],
    group: ChemicalFieldGroup.NAG,
  }, */

  /* {
    fieldName: "W ZINC",
    formattedName: "Fd_Zn",
    aliases: ["ZINC"],
    group: ChemicalFieldGroup.NAG,
  }, */

  {
    fieldName: "W EE%DM",
    formattedName: "Fd_CFat",
    aliases: ["EE", "Fat_EE"],
    group: ChemicalFieldGroup.NAG,
    shownInGasReportTable: true,
  },
  {
    fieldName: "W INSCP%DM",
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W LA%DM",
    aliases: ["lactic"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W MAN%DM",
    aliases: ["mannitol"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W NITRATE",
    aliases: ["N03PPM", "NITRATE"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W LACTOSE",
    aliases: ["LACTOSE"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W BA%DM",
    aliases: ["BUTYRIC", "W BUTYRIC"],
    group: ChemicalFieldGroup.NAG,
  },
  {
    fieldName: "W CRUDEFIBER",
    aliases: ["CRUDEFIBER"],
    group: ChemicalFieldGroup.NAG,
  }

];
export const NutrientFields: ChemicalField[] = [
  {
    types: [],
    fieldName: "Dry matter %",
    formattedName: "Fd_DM",
  },
  {
    types: [],
    fieldName: "Gross Energy kcal/kg",
  },
  {
    types: [],
    fieldName: "Gross Energy MJ/kg",
  },
  {
    types: [],
    fieldName: "DCAD meq/100g",
  },
  {
    types: [],
    fieldName: "Starch digestibility rate %/hr",
  },
  {
    types: [],
    fieldName: "NDF digestibility rate %/hr",
  },
  {
    types: [],
    fieldName: "Calcium %",
    formattedName: "Fd_Ca",
  },
  {
    types: [],
    fieldName: "Phosphorous %",
    formattedName: "Fd_P",
  },
  {
    types: [],
    fieldName: "Potassium %",
    formattedName: "Fd_K",
  },
  {
    types: [],
    fieldName: "Magnesium %",
    formattedName: "Fd_Mg",
  },
  {
    types: [],
    fieldName: "Sodium %",
    formattedName: "Fd_Na",
  },
  {
    types: [],
    fieldName: "Chlorine %",
    formattedName: "Fd_Cl",
  },
  {
    types: [],
    fieldName: "Sulphur %",
    formattedName: "Fd_S",
  },
  {
    types: [],
    fieldName: "Manganese ppm",
    formattedName: "Fd_Mn",
  },
  {
    types: [],
    fieldName: "Zinc ppm",
    formattedName: "Fd_Zn",
  },
  {
    types: [],
    fieldName: "Copper ppm",
    formattedName: "Fd_Cu",
  },
  {
    types: [],
    fieldName: "Iron ppm",
    formattedName: "Fd_Fe",
  },
  {
    types: [],
    fieldName: "Selenium ppm",
    formattedName: "Fd_Se",
  },
  {
    types: [],
    fieldName: "Cobalt ppm",
    formattedName: "Fd_Co",
  },
  {
    types: [],
    fieldName: "Iodine ppm",
    formattedName: "Fd_I",
  },
  {
    types: [],
    fieldName: "C14:0 % of EE",
    formattedName: "Fd_C140_FA",
  },
  {
    types: [],
    fieldName: "Palmitic % of EE",
    formattedName: "Fd_C160_FA",
  },
  {
    types: [],
    fieldName: "Stearic % of EE",
    formattedName: "Fd_C180_FA",
  },
  {
    types: [],
    fieldName: "Oleic % of EE",
    formattedName: "Fd_C181c_FA",
  },
  {
    types: [],
    fieldName: "Linoleic % of EE",
    formattedName: "Fd_C182_FA",
  },
  {
    types: [],
    fieldName: "Linoleic FA g/kg",
  },
  {
    types: [],
    fieldName: "Linolenic % of EE",
    formattedName: "Fd_C183_FA",
  },
  {
    types: [],
    fieldName: "UFL per/100kg",
  },
  {
    types: [],
    fieldName: "UFV per/100kg",
  },
  {
    types: [],
    fieldName: "NeM Kcal/kg",
  },
  {
    types: [],
    fieldName: "NeL Kcal/kg",
  },
  {
    types: [],
    fieldName: "NeG Kcal/kg",
  },
  {
    types: [],
    fieldName: "Starch digestibility %",
    formattedName: "Fd_dcSt",
  },
  {
    types: [],
    fieldName: "Pelleting ease (as a number 1-4)",
  },
  {
    types: [],
    fieldName: "Cellulose %",
  },
  {
    types: [],
    fieldName: "Unsaturated fats %",
  },
  {
    types: [],
    fieldName: "Saturated fats %",
  },
  {
    types: [],
    fieldName: "Omega 3 FA %",
  },
  {
    types: [],
    fieldName: "MA Degradable %",
  },
  {
    types: [],
    fieldName: "Structure value (as a number)",
  },
  {
    types: [],
    fieldName: "Glu Rum (INZO) g/kg",
  },
  {
    types: [],
    fieldName: "GL2H g/kg",
  },
  {
    types: [],
    fieldName: "BACA Nette meq",
  },
  {
    types: [],
    fieldName: "BE (Na+K-Cl) meq",
  },
  {
    types: [],
    fieldName: "Hemicellulose %",
  },
  {
    types: [],
    fieldName: "AZ Ruminal (INZO) g/kg",
  },
  {
    types: [],
    fieldName: "Amidon Bypass g/kg",
  },
  {
    types: [],
    fieldName: "PDIA g/kg",
  },
  {
    types: [],
    fieldName: "PDIE g/kg",
  },
  {
    types: [],
    fieldName: "PDIN g/kg",
  },
  {
    types: [],
    fieldName: "AZ 2H (INZO)",
  },
  {
    types: [],
    fieldName: "Lysine %",
    formattedName: "Fd_Lys_CP",
  },
  {
    types: [],
    fieldName: "Methionine %",
    formattedName: "Fd_Met_CP",
  },
  {
    types: [],
    fieldName: "Vitamin A (KIU/kg)",
    formattedName: "Fd_VitA",
  },
  {
    types: [],
    fieldName: "Vitamin D3 (KIU/kg)",
    formattedName: "Fd_VitD",
  },
  {
    types: [],
    fieldName: "Vitamin E (KIU/kg)",
    formattedName: "Fd_VitE",
  },
  {
    types: [],
    fieldName: "Thiamine (g/kg)"
  },
  {
    types: [],
    fieldName: "Choline Chloride (g/kg)",
    formattedName: "Fd_Choline",
  },
  {
    types: [],
    fieldName: "Biotin ppm",
    formattedName: "Fd_Biotin",
  },
  {
    types: [],
    fieldName: "NDF digestibility 48h % of NDF",
    formattedName: "Fd_DNDF48_NDF",
  },
  {
    types: [],
    fieldName: "Soluble protein % of CP",
    formattedName: "Fd_CPs_CP",
  },
  {
    types: [],
    fieldName: "RUP A fraction % of CP",
    formattedName: "Fd_CPARU",
  },
  {
    types: [],
    fieldName: "RUP B fraction % of CP",
    formattedName: "Fd_CPBRU",
  },
  {
    types: [],
    fieldName: "RUP C fraction % of CP",
    formattedName: "Fd_CPCRU",
  },
  {
    types: [],
    fieldName: "RUP digestibility %",
    formattedName: "Fd_dcRUP",
  },
  {
    types: [],
    fieldName: "RUP degradation rate %/hr",
    formattedName: "Fd_KdRUP",
  },
  {
    types: [],
    fieldName: "RUP base % of CP",
    formattedName: "Fd_RUP_base",
  },
  {
    types: [],
    fieldName: "NPN % of CP",
    formattedName: "Fd_NPN_CP",
  },
  {
    types: [],
    fieldName: "ADF insoluble protein % DM",
    formattedName: "Fd_ADFIP",
  },
  {
    types: [],
    fieldName: "Fatty acid digestibility %",
    formattedName: "Fd_dcFA",
  },
  {
    types: [],
    fieldName: "Arginine % of CP",
    formattedName: "Fd_Arg_CP",
  },
  {
    types: [],
    fieldName: "Histidine % of CP",
    formattedName: "Fd_His_CP",
  },
  {
    types: [],
    fieldName: "Isoleucine % of CP",
    formattedName: "Fd_Ile_CP",
  },
  {
    types: [],
    fieldName: "Leucine % of CP",
    formattedName: "Fd_Leu_CP",
  },
  {
    types: [],
    fieldName: "Phenylalanine % of CP",
    formattedName: "Fd_Phe_CP",
  },
  {
    types: [],
    fieldName: "Threonine % of CP",
    formattedName: "Fd_Thr_CP",
  },
  {
    types: [],
    fieldName: "Tryptophan % of CP",
    formattedName: "Fd_Trp_CP",
  },
  {
    types: [],
    fieldName: "Valine % of CP",
    formattedName: "Fd_Val_CP",
  },
  {
    types: [],
    fieldName: "C12:0 % of FA",
    formattedName: "Fd_C120_FA",
  },
  {
    types: [],
    fieldName: "C16:1 % of FA",
    formattedName: "Fd_C161_FA",
  },
  {
    types: [],
    fieldName: "C18:1 trans % of FA",
    formattedName: "Fd_C181t_FA",
  },
  {
    types: [],
    fieldName: "Other FA % of FA",
    formattedName: "Fd_OtherFA_FA",
  },
  {
    types: [],
    fieldName: "Chromium ppm",
    formattedName: "Fd_Cr",
  },
  {
    types: [],
    fieldName: "Molybdenum ppm",
    formattedName: "Fd_Mo",
  },
  {
    types: [],
    fieldName: "Inorganic phosphorus % of P",
    formattedName: "Fd_Pinorg_P",
  },
  {
    types: [],
    fieldName: "Organic phosphorus % of P",
    formattedName: "Fd_Porg_P",
  },
  {
    types: [],
    fieldName: "Beta-carotene mg/kg",
    formattedName: "Fd_B_Carotene",
  },
  {
    types: [],
    fieldName: "Niacin mg/kg",
    formattedName: "Fd_Niacin",
  },
  {
    types: [],
    fieldName: "Calcium absorption coefficient",
    formattedName: "Fd_acCa_input",
  },
  {
    types: [],
    fieldName: "Phosphorus absorption coefficient",
    formattedName: "Fd_acPtot_input",
  },
  {
    types: [],
    fieldName: "Sodium absorption coefficient",
    formattedName: "Fd_acNa_input",
  },
  {
    types: [],
    fieldName: "Chlorine absorption coefficient",
    formattedName: "Fd_acCl_input",
  },
  {
    types: [],
    fieldName: "Potassium absorption coefficient",
    formattedName: "Fd_acK_input",
  },
  {
    types: [],
    fieldName: "Copper absorption coefficient",
    formattedName: "Fd_acCu_input",
  },
  {
    types: [],
    fieldName: "Iron absorption coefficient",
    formattedName: "Fd_acFe_input",
  },
  {
    types: [],
    fieldName: "Magnesium absorption coefficient",
    formattedName: "Fd_acMg_input",
  },
  {
    types: [],
    fieldName: "Manganese absorption coefficient",
    formattedName: "Fd_acMn_input",
  },
  {
    types: [],
    fieldName: "Zinc absorption coefficient",
    formattedName: "Fd_acZn_input",
  },
];

const shakeChemFieldName = (name: string) =>
  name
    .replace("F ", "")
    .replace("W ", "")
    .replace("%DM", "")
    ?.split("%")?.[0]
    ?.split("/")?.[0]
    ?.split(":Hr")?.[0]
    ?.split(":hr")?.[0] || "";

/** lowercase name/alias/shaken name → canonical fieldName list */
const chemNameToCanonicals = new Map<string, string[]>();
/** canonical fieldName → canonical + aliases */
const chemCanonicalToKeys = new Map<string, string[]>();

const registerChemName = (name: string, canonical: string) => {
  if (!name) return;
  const key = name.toLowerCase();
  const list = chemNameToCanonicals.get(key) || [];
  if (!list.includes(canonical)) list.push(canonical);
  chemNameToCanonicals.set(key, list);
};

ChemicalFields.forEach((field) => {
  const keys = [field.fieldName, ...(field.aliases || [])].filter(Boolean);
  chemCanonicalToKeys.set(field.fieldName, keys);
  keys.forEach((key) => {
    registerChemName(key, field.fieldName);
    const shaken = shakeChemFieldName(key);
    if (shaken && shaken.toLowerCase() !== key.toLowerCase()) {
      registerChemName(shaken, field.fieldName);
    }
  });
});

const lookupCanonicalFieldNames = (name: string): string[] => {
  if (!name) return [];
  return (
    chemNameToCanonicals.get(name.toLowerCase()) ||
    chemNameToCanonicals.get(shakeChemFieldName(name).toLowerCase()) ||
    []
  );
};

const getChemFieldKeyVariants = (name: string): string[] => {
  const canonicals = lookupCanonicalFieldNames(name);
  const keys = new Set<string>([name]);
  canonicals.forEach((canonical) => {
    (chemCanonicalToKeys.get(canonical) || [canonical]).forEach((key) =>
      keys.add(key)
    );
  });
  return [...keys];
};

export const findDbFieldNames = (randomFieldName: string) =>
  lookupCanonicalFieldNames(randomFieldName);

export const findChemFieldForDbField = (
  dbFieldName: string,
  chemicals?: { [key: string]: number | undefined }
) => {
  if (!chemicals || !dbFieldName) return undefined;
  if (Object.prototype.hasOwnProperty.call(chemicals, dbFieldName)) {
    return dbFieldName;
  }
  for (const key of getChemFieldKeyVariants(dbFieldName)) {
    if (Object.prototype.hasOwnProperty.call(chemicals, key)) return key;
  }
  const wanted = lookupCanonicalFieldNames(dbFieldName)[0]?.toLowerCase();
  return Object.keys(chemicals).find((fieldName) => {
    if (fieldName.toLowerCase() === dbFieldName.toLowerCase()) return true;
    if (!wanted) return false;
    return lookupCanonicalFieldNames(fieldName)[0]?.toLowerCase() === wanted;
  });
};

const isDefinedChemNumber = (
  value: number | undefined | null
): value is number => value !== undefined && value !== null && !Number.isNaN(value);

/** Resolve a standard DB field name to its value, including when the sample stores an alias key. */
export const getChemValue = (
  chemicals: { [key: string]: number | undefined } | undefined,
  fieldName: string
): number | undefined => {
  const dbFieldName = ChemicalFields.find(f=>shakeChemFieldName(f.fieldName) == fieldName)?.fieldName || fieldName;
  if (!chemicals || !dbFieldName) return undefined;
  let value: number | undefined;
  if (
    Object.prototype.hasOwnProperty.call(chemicals, dbFieldName) &&
    isDefinedChemNumber(chemicals[dbFieldName])
  ) {
    value = chemicals[dbFieldName];
  } else {
    const actualFieldName = findChemFieldForDbField(dbFieldName, chemicals);
    if (actualFieldName != undefined && isDefinedChemNumber(chemicals[actualFieldName])) {
      value = chemicals[actualFieldName];
    }
  }
  if (value !== undefined || (fieldName !== "NSC" && dbFieldName !== "NSC")) {
    return value;
  }
  const cp = getChemValue(chemicals, "CP");
  const aNDF = getChemValue(chemicals, "aNDF");
  const ash = getChemValue(chemicals, "Ash");
  const fatFromEe = getChemValue(chemicals, "EE");
  const fatEe = isDefinedChemNumber(fatFromEe)
    ? fatFromEe
    : getChemValue(chemicals, "EE");
  if (
    !isDefinedChemNumber(cp) ||
    !isDefinedChemNumber(aNDF) ||
    !isDefinedChemNumber(ash) ||
    !isDefinedChemNumber(fatEe)
  ) {
    return undefined;
  }
  return Number((100 - (cp + aNDF + ash + fatEe)).toFixed(2));
};

/** Write a chem value under the existing alias key if present, and always under the standard DB field name. */
export const setChemValue = (
  chemicals: { [key: string]: number | undefined } | undefined,
  dbFieldName: string,
  value: number
): { [key: string]: number | undefined } => {
  const next = { ...(chemicals || {}) };
  const actualFieldName = findChemFieldForDbField(dbFieldName, next);
  if (actualFieldName && actualFieldName !== dbFieldName) {
    next[actualFieldName] = value;
  }
  next[dbFieldName] = value;
  return next;
};

/** Copy alias-keyed chem values onto standard field names so lookups by fieldName work. */
export const resolveChemicalsToDbFields = (
  chemicals?: { [key: string]: number | undefined }
): { [key: string]: number } => {
  const source = chemicals || {};
  const resolved: { [key: string]: number } = {};
  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    resolved[key] = value;
  });
  ChemicalFields.forEach((field) => {
    if (resolved[field.fieldName] !== undefined) return;
    const value = getChemValue(source, field.fieldName);
    if (value !== undefined && value !== null) {
      resolved[field.fieldName] = value;
    }
  });
  return resolved;
};

export const withResolvedChemicals = <
  T extends { chemicals?: { [key: string]: number | undefined } }
>(
  sample: T
): T => ({
  ...sample,
  chemicals: resolveChemicalsToDbFields(sample.chemicals),
});


export const autoFillChemValues = (chemData: { [key: string]: number }) => {
  let chemicals: { [key: string]: number | undefined } = { ...(chemData || {}) };
  const cp = getChemValue(chemicals, "CP");
  const aNDF = getChemValue(chemicals, "aNDF");
  const ash = getChemValue(chemicals, "Ash");
  const fatEe = getChemValue(chemicals, "EE");
  if (!getChemValue(chemicals, "NSC") && cp && aNDF && ash && fatEe) {
    chemicals = setChemValue(
      chemicals,
      "NSC",
      Number((100 - (cp + aNDF + ash + fatEe)).toFixed(2))
    );
  }
  (["3", "6", "12", "24"] as const).forEach((hr) => {
    const orp = getChemValue(chemicals, `ORP_${hr}`);
    if (!orp) return;
    if (!getChemValue(chemicals, `Acetate_${hr}`)) {
      chemicals = setChemValue(chemicals, `Acetate_${hr}`, getAcetate(orp));
    }
    if (!getChemValue(chemicals, `Propionate_${hr}`)) {
      chemicals = setChemValue(
        chemicals,
        `Propionate_${hr}`,
        getPropionate(orp)
      );
    }
  });
  return chemicals as { [key: string]: number };
};

interface IChemicalDataBaseField extends IDataField {
  required?: boolean;
  dbField: keyof ISampleRecordPopulated;
}

export const ChemicalDataBaseFields: IChemicalDataBaseField[] = [
  {
    fieldName: "Lab SSID",
    dbField: "companyUid",
    aliases: ["SSID", "Lab ID"],
  },
  {
    fieldName: "Sample ID",
    required: true,
    dbField: "sampleNumber",
    aliases: [
      "Sample",
      "sample id",
      "sampleId",
      "sample number",
      "sample no.",
      "sample no",
    ],
  },
  {
    fieldName: "Client",
    required: false,
    dbField: "sampleClient",
    aliases: ["client", "sample client", "company", "dealer"],
  },
  {
    fieldName: "Farm",
    required: false,
    dbField: "sampleSource",
    aliases: ["farm", "sample source"],
  },
  {
    fieldName: "Field Info",
    required: false,
    dbField: "fieldInformation",
    aliases: ["field information", "field info"],
  },
  // {
  //   fieldName: "Barn (sample taken)",
  //   required: false,
  //   dbField: "sampleTakenLocation",
  //   aliases: ["sample taken location", "sample taken"],
  // },
  {
    fieldName: "Barn",
    required: false,
    dbField: "barn",
    aliases: ["barn", "sample barn", "sample barn location"],
  },
  {
    fieldName: "Pen",
    required: false,
    dbField: "pen",
    aliases: ["pen", "sample pen", "sample pen location"],
  },
  {
    fieldName: "Breed",
    required: false,
    dbField: "breed",
    aliases: ["breed", "sample breed"],
  },
  {
    fieldName: "Ration Type",
    required: false,
    dbField: "rationType",
    aliases: ["ration type", "ration"],
  },
  {
    fieldName: "Description",
    required: false,
    dbField: "description",
    aliases: ["desc"],
  },
  {
    fieldName: "Bag Weight",
    required: false,
    dbField: "bagWeight",
    aliases: ["weight", "bag weight", "bag"],
  },
  {
    fieldName: "Bag Weight 48",
    required: false,
    dbField: "bagWeight48",
    aliases: ["bag weight 48", "bag 48"],
  },
  {
    fieldName: "Wet Sample Weight",
    required: false,
    dbField: "wetSampleWeight",
    aliases: ["wet sample weight", "wet sample"],
  },
  {
    fieldName: "Dry Sample Weight",
    required: false,
    dbField: "drySampleWeight",
    aliases: ["dry sample weight", "dry sample"],
  },
  {
    fieldName: "Pan Weight",
    required: false,
    dbField: "panWeight",
    aliases: ["pan weight", "pan"],
  },
  {
    fieldName: "Dataset",
    dbField: "dataset",
  },
  {
    fieldName: "Country",
    dbField: "country",
  },
  {
    fieldName: "Region",
    dbField: "region",
  },
  {
    fieldName: "Latitude",
    dbField: "latitude",
    aliases: ["lat"],
  },
  {
    fieldName: "Longitude",
    dbField: "longitude",
    aliases: ["lng"],
  },
  {
    fieldName: "Diet Region",
    dbField: "dietRegion",
    aliases: ["diet region", "diet-region", "diet-region-code"],
  },
  {
    fieldName: "Year",
    dbField: "year",
  },
  {
    fieldName: "Crop Type",
    required: true,
    dbField: "type",
    aliases: ["type"],
  },
  {
    fieldName: "Report Type",
    dbField: "reportType",
  },
  {
    fieldName: "Sample Taken Date",
    dbField: "sampleTakenDate",
    aliases: ["date", "Date", "sample date", "sampled date"],
  },
  {
    fieldName: "Product",
    dbField: "product",
    aliases: ["Product", "pc"],
  },
  {
    fieldName: "Application",
    dbField: "application",
    aliases: ["app"],
  },
  {
    fieldName: "Data Source",
    required: false,
    dbField: "dataSource",
    aliases: ["data source"],
  },
];

export const NumberRoundDisabledDbFieldNames = ["Fast_HR", "F Fmax/Hr", "Slow_HR", "F Smax/Hr"];

export interface IChemicalAnalysisItem {
  _id?: string;
  ssid: number;
  sampleNumber: string;
  sampleSource?: string;
  fieldInformation?: string;
  sampleTakenLocation?: string;
  pen?: string;
  barn?: string;
  breed?: string;
  rationType?: string;
  dataSource?: string;
  sampleClient?: string;
  bagWeight?: number;
  dataset?: string;
  country?: string;
  region?: string;
  year?: number;
  type?: CropTypes;
  reportType?: string;
  chemicals?: { [key: string]: number };
}

export const defaultFeedToCategory = [
  {
    feed: "Zinc, organic generic",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calf grower 14CP (Fed to calves only)",
    category: "Energy Source",
  },
  {
    feed: "Calf grower 16CP (Fed to calves only)",
    category: "Energy Source",
  },
  {
    feed: "Calf starter 18CP high starch (Fed to calves only)",
    category: "Energy Source",
  },
  {
    feed: "Calf starter 18CP low starch (Fed to calves only)",
    category: "Energy Source",
  },
  {
    feed: "Calf starter 22CP high starch (Fed to calves only)",
    category: "Energy Source",
  },
  {
    feed: "Calf starter 22CP med starch (Fed to calves only)",
    category: "Energy Source",
  },
  {
    feed: "Casein",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Milk replacer 20 CP 20 fat",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Milk replacer 26 CP 17 fat",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Milk replacer 28 CP 17.5 fat",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Skim milk, fresh",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Skim milk, powder",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Whey protein concentrate",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Whey, delactosed",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Whey, fresh",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Whey, permeate",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Whole milk",
    category: "Calf Liquid Feed",
  },
  {
    feed: "Alfalfa meal",
    category: "Plant Protein",
  },
  {
    feed: "Almond hulls",
    category: "By-Product/Other",
  },
  {
    feed: "Ammonium chloride",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Ammonium phosphate (dibasic)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Ammonium phosphate (monobasic)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Ammonium sulfate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Apple pomace or byproduct, wet",
    category: "By-Product/Other",
  },
  {
    feed: "Bakery byproduct",
    category: "By-Product/Other",
  },
  {
    feed: "Bakery byproduct, bread waste",
    category: "By-Product/Other",
  },
  {
    feed: "Bakery byproduct, cereal",
    category: "By-Product/Other",
  },
  {
    feed: "Bakery byproduct, cookies",
    category: "By-Product/Other",
  },
  {
    feed: "Barley grain, dry, ground",
    category: "Energy Source",
  },
  {
    feed: "Barley grain, steam rolled",
    category: "Energy Source",
  },
  {
    feed: "Barley hay",
    category: "Grain Crop Forage",
  },
  {
    feed: "Barley malt sprouts",
    category: "By-product/Other",
  },
  {
    feed: "Barley silage, headed",
    category: "Grain Crop Forage",
  },
  {
    feed: "Barley silage, mid-maturity",
    category: "Grain Crop Forage",
  },
  {
    feed: "Barley silage, vegetative",
    category: "Grain Crop Forage",
  },
  {
    feed: "Beet pulp, dry",
    category: "By-Product/Other",
  },
  {
    feed: "Beet pulp, dry, molasses added",
    category: "By-Product/Other",
  },
  {
    feed: "Beet pulp, wet",
    category: "By-Product/Other",
  },
  {
    feed: "Bermudagrass hay",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Bermudagrass silage, mature",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Bermudagrass silage, mid-mtr",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Blood meal, high dRUP",
    category: "Animal Protein",
  },
  {
    feed: "Blood meal, low dRUP",
    category: "Animal Protein",
  },
  {
    feed: "Bone meal",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Brewers grains, dry",
    category: "By-Product/Other",
  },
  {
    feed: "Brewers grains, wet",
    category: "By-Product/Other",
  },
  {
    feed: "Brewers yeast, dry",
    category: "By-Product/Other",
  },
  {
    feed: "Brewers yeast, wet",
    category: "By-Product/Other",
  },
  {
    feed: "Calcium carbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calcium chloride",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calcium chloride (2H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calcium hydroxide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calcium iodate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calcium oxide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calcium phosphate (di)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calcium phosphate (mono)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Calcium soaps",
    category: "Fatty Acid Supplement",
  },
  {
    feed: "Calcium sulfate (2H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Candy (not chocolate) byproduct",
    category: "By-Product/Other",
  },
  {
    feed: "Candy byproduct, high protein",
    category: "By-Product/Other",
  },
  {
    feed: "Canola meal",
    category: "Plant Protein",
  },
  {
    feed: "Canola seed, ground",
    category: "Plant Protein",
  },
  {
    feed: "Chocolate byproduct",
    category: "By-Product/Other",
  },
  {
    feed: "Citrus pulp, dry",
    category: "By-Product/Other",
  },
  {
    feed: "Citrus pulp, wet",
    category: "By-Product/Other",
  },
  {
    feed: "Cobalt carbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Cobalt carbonate (6H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Cobalt chloride (6H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Cobalt organic generic",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Cobalt sulfate (7H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Cool season grass hay, mature",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Cool season grass hay, mid-mtr",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Cool season grass silage",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Copper chloride (2H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Copper organic, generic",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Copper oxide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Copper sulfate (5H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Corn and cob meal, dry",
    category: "Energy Source",
  },
  {
    feed: "Corn cobs",
    category: "By-Product/Other",
  },
  {
    feed: "Corn germ",
    category: "Plant Protein",
  },
  {
    feed: "Corn germ meal",
    category: "Plant Protein",
  },
  {
    feed: "Corn gluten feed, dry",
    category: "By-Product/Other",
  },
  {
    feed: "Corn gluten feed, wet",
    category: "By-Product/Other",
  },
  {
    feed: "Corn gluten meal",
    category: "Plant Protein",
  },
  {
    feed: "Corn grain dry, coarse grind",
    category: "Energy Source",
  },
  {
    feed: "Corn grain dry, fine grind",
    category: "Energy Source",
  },
  {
    feed: "Corn grain dry, medium grind",
    category: "Energy Source",
  },
  {
    feed: "Corn grain HM, coarse grind",
    category: "Energy Source",
  },
  {
    feed: "Corn grain HM, fine grind",
    category: "Energy Source",
  },
  {
    feed: "Corn grain screenings",
    category: "Energy Source",
  },
  {
    feed: "Corn grain, steam-flaked",
    category: "Energy Source",
  },
  {
    feed: "Corn hominy feed",
    category: "Energy Source",
  },
  {
    feed: "Corn silage, immature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Corn silage, mature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Corn silage, typical",
    category: "Grain Crop Forage",
  },
  {
    feed: "Corn stalks, ensiled, high DM",
    category: "Grain Crop Forage",
  },
  {
    feed: "Corn stalks, ensiled, low DM",
    category: "Grain Crop Forage",
  },
  {
    feed: "Corn, ear w husk, stlk, hi fbr",
    category: "Energy Source",
  },
  {
    feed: "Corn, ear, huks, stlk, low fbr",
    category: "Energy Source",
  },
  {
    feed: "Cotton gin trash",
    category: "By-Product/Other",
  },
  {
    feed: "Cottonseed hulls",
    category: "By-Product/Other",
  },
  {
    feed: "Cottonseed meal",
    category: "Plant Protein",
  },
  {
    feed: "Cottonseed, whole",
    category: "Plant Protein",
  },
  {
    feed: "DDGS, high fat",
    category: "By-Product/Other",
  },
  {
    feed: "DDGS, high protein",
    category: "By-Product/Other",
  },
  {
    feed: "DDGS, low fat",
    category: "By-Product/Other",
  },
  {
    feed: "DGS modified",
    category: "By-Product/Other",
  },
  {
    feed: "DGS wet",
    category: "By-Product/Other",
  },
  {
    feed: "Distillers solubles",
    category: "By-Product/Other",
  },
  {
    feed: "Dolomite limestone (magnesium)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "EDDI",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Fat, canola oil",
    category: "Fat Supplement",
  },
  {
    feed: "Fat, corn oil",
    category: "Fat Supplement",
  },
  {
    feed: "Fat, cottonseed oil",
    category: "Fat Supplement",
  },
  {
    feed: "Fat, flaxseed oil",
    category: "Fat Supplement",
  },
  {
    feed: "Fat, lard",
    category: "Fat Supplement",
  },
  {
    feed: "Fat, safflower oil",
    category: "Fat Supplement",
  },
  {
    feed: "Fat, soybean oil",
    category: "Fat Supplement",
  },
  {
    feed: "Fat, sunflower oil",
    category: "Fat Supplement",
  },
  {
    feed: "Fat, tallow",
    category: "Fat Supplement",
  },
  {
    feed: "Feather meal",
    category: "Animal Protein",
  },
  {
    feed: "Ferrous carbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Ferrous sulfate (7H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Ferrous sulfate (H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Fish meal",
    category: "Animal Protein",
  },
  {
    feed: "Flaxseed",
    category: "Plant Protein",
  },
  {
    feed: "Flaxseed meal",
    category: "Plant Protein",
  },
  {
    feed: "Fruit and vegetable byprod, wet",
    category: "By-Product/Other",
  },
  {
    feed: "Glycerol",
    category: "Energy Source",
  },
  {
    feed: "Grain screenings, source unknwn",
    category: "By-Product/Other",
  },
  {
    feed: "Grain sorghum hay",
    category: "Grain Crop Forage",
  },
  {
    feed: "Grain sorghum silage, mature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Grain sorghum silage, midmtr",
    category: "Grain Crop Forage",
  },
  {
    feed: "Grass legume mixt, grass slg",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Grass lg mixt, grass hay, mid",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Grass lg mixt, grass hay, mtr",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Grass lg mixt, leg. hay, mtr",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Grass lg mixt, leg., hay, immtr",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Grass lg mixt, legume slg",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Grass lg mixt, mix hay",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Grass lg mixt, mix silage",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Iron oxide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Iron, organic generic",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Legume hay, immature",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Legume hay, mature",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Legume hay, mid-maturity",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Legume silage, immature",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Legume silage, mid-maturity",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Limestone",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Magnesium carbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Magnesium chloride (6H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Magnesium hydroxide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Magnesium oxide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Magnesium sulfate (7H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Manganese carbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Manganese chloride",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Manganese chloride (4H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Manganese organic generic",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Manganese oxide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Manganese sulfate (5H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Manganese sulfate (H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Meat and bone meal, porcine",
    category: "Animal Protein",
  },
  {
    feed: "Millet hay",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Millet silage",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Molasses",
    category: "Energy Source",
  },
  {
    feed: "Oat grain",
    category: "Energy Source",
  },
  {
    feed: "Oat hay mid-maturity",
    category: "Grain Crop Forage",
  },
  {
    feed: "Oat hulls",
    category: "By-Product/Other",
  },
  {
    feed: "Oat silage, immature",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Oat silage, mid-maturity",
    category: "Grain Crop Forage",
  },
  {
    feed: "Oystershell",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Pasture, grass",
    category: "Pasture",
  },
  {
    feed: "Pasture, grass-legume mixture",
    category: "Pasture",
  },
  {
    feed: "Pasture, legume",
    category: "Pasture",
  },
  {
    feed: "Pea hay",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Pea silage",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Peanut hay",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Peanut hulls",
    category: "By-Product/Other",
  },
  {
    feed: "Peanut meal, expellers",
    category: "Plant Protein",
  },
  {
    feed: "Peanut skins",
    category: "By-Product/Other",
  },
  {
    feed: "Peanuts",
    category: "Plant Protein",
  },
  {
    feed: "Peas",
    category: "Plant Protein",
  },
  {
    feed: "Phosphate, Curacao",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Phosphate, defluorinated",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Phosphate, monosodium (H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Phosphoric acid",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Pineapple cannery waste",
    category: "By-Product/Other",
  },
  {
    feed: "Potassium bicarbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Potassium carbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Potassium chloride",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Potassium iodide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Potassium sulfate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Potato byproduct",
    category: "By-Product/Other",
  },
  {
    feed: "Poultry byproduct meal",
    category: "Animal Protein",
  },
  {
    feed: "Rice bran",
    category: "By-Product/Other",
  },
  {
    feed: "Rice bran, defatted",
    category: "By-Product/Other",
  },
  {
    feed: "Rice hulls",
    category: "By-Product/Other",
  },
  {
    feed: "Rice silage, headed",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rice silage, vegetative",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rice, grain",
    category: "Energy Source",
  },
  {
    feed: "Rumen Protected Arg",
    category: "Additive",
  },
  {
    feed: "Rumen Protected His",
    category: "Additive",
  },
  {
    feed: "Rumen Protected Ile",
    category: "Additive",
  },
  {
    feed: "Rumen Protected Leu",
    category: "Additive",
  },
  {
    feed: "Rumen Protected Lys",
    category: "Additive",
  },
  {
    feed: "Rumen Protected Met",
    category: "Additive",
  },
  {
    feed: "Rumen Protected Phe",
    category: "Additive",
  },
  {
    feed: "Rumen Protected Thr",
    category: "Additive",
  },
  {
    feed: "Rumen Protected Trp",
    category: "Additive",
  },
  {
    feed: "Rumen Protected Val",
    category: "Additive",
  },
  {
    feed: "Rye annual fresh, immature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rye annual fresh, mid-maturity",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rye annual hay, immature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rye annual hay, mature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rye annual hay, mid-maturity",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rye annual silage, immature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rye annual silage, mature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rye annual silage, mid-maturity",
    category: "Grain Crop Forage",
  },
  {
    feed: "Rye grain",
    category: "Energy Source",
  },
  {
    feed: "Safflower meal",
    category: "Plant Protein",
  },
  {
    feed: "Selenate, sodium (10 H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Selenite, sodium",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Selenium yeast, generic",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Sodium bicarbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Sodium carbonate (H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Sodium chloride (salt)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Sodium sesquicarbonate (2H2O)",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Sodium sulfate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Sodium tripolyphosphate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Sorghum forage silage, immature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Sorghum forage silage, mature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Sorghum grain, dry, ground",
    category: "Energy Source",
  },
  {
    feed: "Sorghum grain, steam flaked",
    category: "Energy Source",
  },
  {
    feed: "Sorghum hay",
    category: "Grain Crop Forage",
  },
  {
    feed: "Sorghum soybean silage",
    category: "Grain Crop Forage",
  },
  {
    feed: "Sorghum-sudangrass hay",
    category: "Grain Crop Forage",
  },
  {
    feed: "Sorghum-sudangrass silage",
    category: "Grain Crop Forage",
  },
  {
    feed: "Soybean hay",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Soybean hulls",
    category: "By-Product/Other",
  },
  {
    feed: "Soybean meal, expellers",
    category: "Plant Protein",
  },
  {
    feed: "Soybean meal, extruded",
    category: "Plant Protein",
  },
  {
    feed: "Soybean meal, solvent 48CP",
    category: "Plant Protein",
  },
  {
    feed: "Soybean silage",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Soybeans, whole raw",
    category: "Plant Protein",
  },
  {
    feed: "Soybeans, whole roasted",
    category: "Plant Protein",
  },
  {
    feed: "Spelt grain",
    category: "Energy Source",
  },
  {
    feed: "Sudangrass hay, mature",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Sudangrass hay, mid-maturity",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Sudangrass silage, mature",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Sudangrass silage, mid-maturity",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Sugar",
    category: "Sugar/Sugar Alcohol",
  },
  {
    feed: "Sugarcane bagasse hay",
    category: "Grain Crop Forage",
  },
  {
    feed: "Sugarcane bagasse silage",
    category: "Grain Crop Forage",
  },
  {
    feed: "Sunflower meal",
    category: "Plant Protein",
  },
  {
    feed: "Sunflower seed",
    category: "Plant Protein",
  },
  {
    feed: "Sunflower silage",
    category: "Grass/Legume Forage",
  },
  {
    feed: "Sweet corn cannery waste",
    category: "By-Product/Other",
  },
  {
    feed: "Tapioca (Cassava)",
    category: "Energy Source",
  },
  {
    feed: "Tomato pomace",
    category: "By-Product/Other",
  },
  {
    feed: "Triticale grain",
    category: "Energy Source",
  },
  {
    feed: "Triticale hay",
    category: "Grain Crop Forage",
  },
  {
    feed: "Triticale plus pea silage",
    category: "Grain Crop Forage",
  },
  {
    feed: "Triticale silage, mature",
    category: "Grain Crop Forage",
  },
  {
    feed: "Triticale silage, mid-maturity",
    category: "Grain Crop Forage",
  },
  {
    feed: "Urea",
    category: "Additive",
  },
  {
    feed: "Vit A premix",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Vit D premix",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Vit E premix",
    category: "Vitamin/Mineral",
  },
  {
    feed: "VitTM Premix, generic",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Wheat bran",
    category: "By-Product/Other",
  },
  {
    feed: "Wheat grain",
    category: "Energy Source",
  },
  {
    feed: "Wheat hay, headed",
    category: "Grain Crop Forage",
  },
  {
    feed: "Wheat hay, vegetative",
    category: "Grain Crop Forage",
  },
  {
    feed: "Wheat middlings",
    category: "By-Product/Other",
  },
  {
    feed: "Wheat silage, HEADED",
    category: "Grain Crop Forage",
  },
  {
    feed: "Wheat silage, VEGETATIVE",
    category: "Grain Crop Forage",
  },
  {
    feed: "Wheat straw",
    category: "Grain Crop Forage",
  },
  {
    feed: "Whey, dry",
    category: "Animal Protein",
  },
  {
    feed: "Whey, wet",
    category: "Animal Protein",
  },
  {
    feed: "Zinc carbonate",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Zinc chloride",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Zinc oxide",
    category: "Vitamin/Mineral",
  },
  {
    feed: "Zinc sulfate (H2O)",
    category: "Vitamin/Mineral",
  },
];

export type SimilarDefaultFeed = {
  feed: string;
  category: string;
  exact: boolean;
};

function normalizeFeedName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function editDistance(left: string, right: string): number {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let i = 1; i <= left.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, previous[j - 1] + cost);
    }
    previous = current;
  }
  return previous[right.length];
}

function nameSimilarity(left: string, right: string): number {
  if (!left || !right) return 0;
  return 1 - editDistance(left, right) / Math.max(left.length, right.length);
}

function similarFeedScore(query: string, feed: string): number {
  if (query === feed) return 1000;
  let score = 0;
  const sharesPrefix = feed.startsWith(query) || query.startsWith(feed);
  const contains = query.includes(feed) || feed.includes(query);
  if (sharesPrefix || contains) {
    const shorter = Math.min(query.length, feed.length);
    const longer = Math.max(query.length, feed.length);
    score = (sharesPrefix ? 800 : 700) + (100 * shorter) / longer;
  }
  const full = nameSimilarity(query, feed);
  if (full >= 0.55) score = Math.max(score, 500 + 100 * full);
  const queryTokens = query.split(" ");
  const feedTokens = feed.split(" ");
  if (queryTokens.length && feedTokens.length) {
    let matched = 0;
    let strong = 0;
    queryTokens.forEach((token) => {
      let best = 0;
      feedTokens.forEach((other) => {
        if (token === other || token.includes(other) || other.includes(token)) {
          best = Math.max(
            best,
            Math.min(token.length, other.length) / Math.max(token.length, other.length)
          );
        } else {
          best = Math.max(best, nameSimilarity(token, other));
        }
      });
      if (best >= 0.8) strong += 1;
      matched += best;
    });
    if (strong === queryTokens.length) {
      const average = matched / queryTokens.length;
      const coverage = Math.min(query.length, feed.length) / Math.max(query.length, feed.length);
      score = Math.max(score, 400 + 200 * average * coverage);
    }
  }
  return score;
}

/** Closest published NASEM feed for a free-typed ingredient name. */
export function matchDefaultFeed(feedName: string): SimilarDefaultFeed | undefined {
  const query = normalizeFeedName(feedName);
  if (query.length < 3) return undefined;
  let best: { feed: string; category: string; score: number } | undefined;
  defaultFeedToCategory.forEach((item) => {
    const score = similarFeedScore(query, normalizeFeedName(item.feed));
    if (
      !best ||
      score > best.score ||
      (score === best.score && item.feed.length < best.feed.length)
    ) {
      best = { feed: item.feed, category: item.category, score };
    }
  });
  if (!best || best.score < 450) return undefined;
  return {
    feed: best.feed,
    category: best.category === "By-product/Other" ? "By-Product/Other" : best.category,
    exact: normalizeFeedName(best.feed) === query,
  };
}

export function similarFeedCategorySuggestion(
  feedName: string
): { text: string; category: string } | undefined {
  const similar = matchDefaultFeed(feedName);
  if (!similar) return undefined;
  return {
    text: `Similar feed: ${similar.feed}'s category is ${similar.category}`,
    category: similar.category,
  };
}
